import { Pager } from "@progress/kendo-react-data-tools";
import { Loader } from "@progress/kendo-react-indicators";
import { TextBox } from "@progress/kendo-react-inputs";
import { TileLayout } from "@progress/kendo-react-layout";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import CreateButton from "../Buttons/CreateButton.jsx";
import DeleteButton from "../Buttons/DeleteButton.jsx";
import EditButton from "../Buttons/EditButton.jsx";
import { allRoles, userData } from "../Common/Store/CommonSignals.jsx";
import ConfirmDeleteDialog from "../Notification/ConfirmDeleteDialog";
import { deleteRole } from "./RolesSlice";

const CreateRoleBtn = ({ navigate }) => {
  return (
    <CreateButton
      component="Create Role"
      onClick={() => navigate("/roles/create")}
    />
  );
};

function ViewRoles() {
  const [deleting, setDeleting] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState();
  const groups = userData.value?.groups;
  const [search, setSearch] = useState("");
  const itemsPerPage = 4;
  const initialPageState = {
    skip: 0,
    take: itemsPerPage,
    buttonCount: 5,
    type: "numeric",
    info: true,
    pageSizes: true,
    previousNext: true,
    responsive: true,
  };
  const [pageState, setPageState] = useState(initialPageState);
  let { skip, take } = pageState;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userEmail = userData.value?.userEmail;
  const error = useSelector((state) => state.roles.error);

  useEffect(() => {
    if (error) {
      console.error("Error fetching roles:", error);
    }
  }, [error]);

  const HandleDeleteRole = async (id) => {
    setSelectedTemplateId(id);
    setDeleting(id);
    setShowDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setDeleting(null);
  };

  const handleDeleteDialogConfirm = () => {
    setShowDeleteDialog(false);
    setDeleting(null);
    dispatch(deleteRole({ roleId: selectedTemplateId, userName: userEmail }));
  };

  const filteredRoles = Array.isArray(allRoles.value)
    ? allRoles.value.filter((role) =>
        Object.values(role).some((value) =>
          value?.toString().toLowerCase().includes(search.toLowerCase())
        )
      )
    : [];

  const items = filteredRoles.map((role) => ({
    key: role.id,
    resizable: false,
    reordorable: false,
    style: { cursor: "default" },
    header: (
      <div>
        <div className="dataset-tile-header">
          <strong>{role.roleName}</strong>
          <div>
            {deleting === role.id ? (
              <Loader
                type="pulsing"
                themeColor="primary"
              />
            ) : (
              <div
                key={`${role.id}`}
                className="align-columns"
              >
                <DeleteButton
                  component="Delete Role"
                  name={"btnRolesDelete_" + role.roleName}
                  onClick={() => HandleDeleteRole(role.id)}
                />
                <EditButton
                  component="Edit Role"
                  name={"btnRolesEdit_" + role.roleName}
                  onClick={() => navigate(`/roles/edit?roleId=${role.id}`)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="dataset-tile-subheader">{role.id}</div>
      </div>
    ),
    body: (
      <div className="k-h-full k-d-flex k-flex-col k-overflow-y-auto">
        <div className="k-var--neutral-30 k-rounded-lg k-overflow-y-auto k-flex-grow">
          <div>
            <strong className="k-ml-3">Groups:</strong>
            {role.groupId.map((groupId) => (
              <div
                key={uuidv4()}
                className="k-ml-8"
              >
                {groups.map((g) => {
                  if (g.id === groupId) {
                    return g.displayName;
                  }
                  return null;
                })}
              </div>
            ))}
          </div>
          <div>
            <strong className="k-ml-3">ViewOnly Components:</strong>{" "}
            {role.permissions.map((item) => (
              <div
                key={uuidv4()}
                className="k-ml-8"
              >
                {item.action === "ViewOnly" ? (
                  item.resourcesName.map((item) => <div key={item}>{item}</div>)
                ) : (
                  <div></div>
                )}
              </div>
            ))}
            <strong className="k-ml-3">Modify Components:</strong>{" "}
            {role.permissions.map((item) => (
              <div
                key={uuidv4()}
                className="k-ml-8"
              >
                {item.action === "Modify" ? (
                  item.resourcesName.map((item) => <div key={item}>{item}</div>)
                ) : (
                  <div></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  }));

  const paginatedItems = items.slice(skip, skip + take);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };
  const handlePageChange = (event) => {
    const { skip, take } = event;
    setPageState({
      ...pageState,
      skip: skip,
      take: take,
    });
  };

  return (
    <div className="view-container">
      <TextBox
        name="txtRolesSearchBar"
        className="tilelayout-searchbar"
        prefix={() => CreateRoleBtn({ navigate })}
        placeholder="Search by any field..."
        onChange={handleSearch}
        value={search}
      />
      <TileLayout
        className="tile-layout"
        items={paginatedItems}
        columns={4}
        rows={1}
        autoFlow="row dense"
        ignoreDrag={(e) => {
          return e.target;
        }}
      />
      <Pager
        className="view-pager"
        skip={skip}
        take={take}
        total={items.length}
        buttonCount={pageState.buttonCount}
        info={pageState.info}
        type={pageState.type}
        previousNext={pageState.previousNext}
        onPageChange={handlePageChange}
      />
      {showDeleteDialog && (
        <ConfirmDeleteDialog
          content={"role"}
          handleCancelDelete={handleDeleteDialogClose}
          handleConfirmDelete={handleDeleteDialogConfirm}
        />
      )}
    </div>
  );
}

export default ViewRoles;
